import React, { useRef, useState, useEffect, forwardRef } from "react"
import styled from "styled-components"
import Image from "gatsby-image"
import { useWindowSize } from "react-use"

import ButtonArrow, { primaryButtonArrowStyles } from "./Buttons/ButtonArrow"
import BootcampInfo from "./Popup/BootcampInfo"
import Tags from "./Tags"
import TextRenderer from "./TextRenderer"

import { generatePath } from "./../utils/helpers"

const StyledItem = styled.li`
  ${props =>
    props.disabled
      ? `
        pointer-events: none;
    `
      : `
        &:hover {
            img {
                transform: scale(1.05);
            }
        }
    `}
`

const ImageWrapper = styled.div`
  display: block;

  position: relative;

  margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.5);
  padding-bottom: 116.66%;

  ${props => props.theme.above.desktop`
        margin-bottom: ${props.theme.sizes.desktop};
    `}
`

const StyledImage = styled(Image)`
  ${props => props.theme.styles.element.fill};

  position: absolute !important;

  img {
    transition: transform 5s ease-out !important;
  }
`

const Header = styled.div`
  position: relative;

  margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.5);

  ${props => props.theme.above.desktop`
        margin-bottom: calc(${props.theme.sizes.desktop} / 2);
    `}
`

const Heading = styled.h4`
  display: block;

  font-family: ${props => props.theme.fontFamilies.plainLight};
  font-size: ${props => props.theme.fontSizes.mobile.h5};
  line-height: 1.3;

  ${props => props.theme.above.desktop`
        font-size: ${props.theme.fontSizes.desktop.h5};
    `}
`

const HeadingButton = styled.button`
  display: block;

  padding: 0;

  border: 0;
  background: transparent;
  color: inherit;

  font-family: ${props => props.theme.fontFamilies.plainLight};
  font-size: ${props => props.theme.fontSizes.mobile.h5};
  line-height: 1.3;
  text-align: left;

  cursor: ${props => (props.$canHover ? "help" : "pointer")};

  &:hover,
  &:focus {
    outline: none;
    text-decoration: underline;
    text-decoration-color: ${props => props.theme.colors.orange};
    text-underline-offset: 0.12em;
  }

  ${props => props.theme.above.desktop`
        font-size: ${props.theme.fontSizes.desktop.h5};
    `}
`

const Description = styled(TextRenderer)`
  margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.5) !important;

  font-size: ${props => props.theme.fontSizes.mobile.m};

  ${props => props.theme.above.desktop`
        margin-bottom: calc(${props.theme.sizes.desktop} / 1.5) !important;

        font-size: ${props.theme.fontSizes.desktop.p};

        li {
            p {
                margin-bottom: 0 !important;
            }
        }
    `}
`

const AvailabilityLabel = styled.span`
  display: inline-flex;
  align-items: center;

  margin-bottom: calc(${props => props.theme.sizes.mobile} / 1.5);
  padding: ${props => props.theme.mobileVW(9)}
    ${props => props.theme.mobileVW(14)};

  background-color: ${props => props.theme.colors.orange};
  color: ${props => props.theme.colors.dark};

  font-family: ${props => props.theme.fontFamilies.nbBold};
  font-size: ${props => props.theme.fontSizes.mobile.s};
  line-height: 1;
  text-transform: uppercase;

  ${props => props.theme.above.desktop`
        margin-bottom: calc(${props.theme.sizes.desktop} / 1.5);
        padding: ${props.theme.desktopVW(9)} ${props.theme.desktopVW(14)};

        font-size: ${props.theme.fontSizes.desktop.s};
    `}
`

const Footer = styled.div`
  ${props => props.theme.styles.flexBox.horCen};
`

const StyledButtonArrow = styled(ButtonArrow)`
  ${props => props.primary && primaryButtonArrowStyles}
`

const Category = styled.span`
  display: block;

  margin-bottom: calc(${props => props.theme.sizes.mobile} / 4);

  color: ${props => props.theme.colors.orange};

  font-family: ${props => props.theme.fontFamilies.nbRegular};
  font-size: ${props => props.theme.fontSizes.mobile.s};
  line-height: 1.5;

  text-transform: uppercase;

  ${props => props.theme.above.desktop`
        margin-bottom: calc(${props.theme.sizes.desktop} / 3);

        font-size: ${props.theme.fontSizes.desktop.m};
    `}
`

const StyledTags = styled(Tags)`
  position: absolute;

  z-index: 1;

  justify-content: flex-end;

  ${props => props.theme.below.desktop`
        display: none;
    `}

  ${props => props.theme.above.desktop`
        .tag {
            margin-bottom: 0;
        }

        bottom: calc(${props.theme.sizes.desktop} / 1.5);
        right: calc(${props.theme.sizes.desktop});
    `}
`

const MS_PER_DAY = 24 * 60 * 60 * 1000
const LAST_SPOTS_TRIGGER_DAYS = 21
const HOVER_OPEN_DELAY_MS = 550
const HOVER_MEDIA_QUERY = "(hover: hover) and (pointer: fine)"

const getStartOfDay = date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate())

const parseContentfulDate = date => {
  if (!date) return null

  const dateParts = date
    .split("T")[0]
    .split("-")
    .map(Number)

  if (dateParts.length === 3 && dateParts.every(Boolean)) {
    return new Date(dateParts[0], dateParts[1] - 1, dateParts[2])
  }

  const parsedDate = new Date(date)

  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate
}

const isLastSpotsAvailable = startdateRaw => {
  const startDate = parseContentfulDate(startdateRaw)

  if (!startDate) return false

  const today = getStartOfDay(new Date())
  const bootcampStartDate = getStartOfDay(startDate)
  const daysUntilStart = (bootcampStartDate - today) / MS_PER_DAY

  return daysUntilStart >= 0 && daysUntilStart <= LAST_SPOTS_TRIGGER_DAYS
}

const CalendarItem = ({ lang, data }, ref) => {
  const { width: windowWidth } = useWindowSize()
  const [showLastSpotsLabel, setShowLastSpotsLabel] = useState(false)
  const [isBootcampInfoOpen, setIsBootcampInfoOpen] = useState(false)
  const [canHover, setCanHover] = useState(false)

  const {
    title,
    type,
    thumbnail,
    excerpt,
    startdate,
    startdateRaw,
    enddate,
    year,
    startDateFormInput,
    endDateFormInput,
    location,
  } = data

  const hasBootcampInfo = type == "Bootcamp"

  // Refs
  const buttonRef = useRef(null)
  const bootcampTriggerRef = useRef(null)
  const hoverOpenTimeoutRef = useRef(null)

  let tagItems = []
  tagItems.push(type)
  tagItems.push(year)

  useEffect(() => {
    setShowLastSpotsLabel(
      type == "Bootcamp" && isLastSpotsAvailable(startdateRaw)
    )
  }, [type, startdateRaw])

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined

    const hoverMediaQuery = window.matchMedia(HOVER_MEDIA_QUERY)
    const updateHoverCapability = () => setCanHover(hoverMediaQuery.matches)

    updateHoverCapability()

    if (hoverMediaQuery.addEventListener) {
      hoverMediaQuery.addEventListener("change", updateHoverCapability)
    } else {
      hoverMediaQuery.addListener(updateHoverCapability)
    }

    return () => {
      if (hoverMediaQuery.removeEventListener) {
        hoverMediaQuery.removeEventListener("change", updateHoverCapability)
      } else {
        hoverMediaQuery.removeListener(updateHoverCapability)
      }
    }
  }, [])

  useEffect(
    () => () => {
      if (hoverOpenTimeoutRef.current !== null) {
        clearTimeout(hoverOpenTimeoutRef.current)
      }
    },
    []
  )

  const clearBootcampInfoHover = () => {
    if (hoverOpenTimeoutRef.current === null) return

    clearTimeout(hoverOpenTimeoutRef.current)
    hoverOpenTimeoutRef.current = null
  }

  const openBootcampInfo = () => {
    clearBootcampInfoHover()
    setIsBootcampInfoOpen(true)
  }

  const handleBootcampInfoMouseEnter = () => {
    if (!canHover) return

    clearBootcampInfoHover()
    hoverOpenTimeoutRef.current = setTimeout(() => {
      hoverOpenTimeoutRef.current = null
      setIsBootcampInfoOpen(true)
    }, HOVER_OPEN_DELAY_MS)
  }

  const handleBootcampInfoClick = () => {
    if (!canHover) openBootcampInfo()
  }

  const handleBootcampInfoKeyDown = event => {
    if (event.key !== "Enter" && event.key !== " ") return

    event.preventDefault()
    openBootcampInfo()
  }

  return (
    <StyledItem ref={ref}>
      {/* <ImageWrapper>
                <StyledImage fluid={thumbnail.fluid} alt={thumbnail.title} />
                <StyledTags data={tagItems}></StyledTags>
            </ImageWrapper> */}
      <Header>
        <Category>
          {`${startdate.replace(".", "")} - ${enddate.replace(".", "")}`} /{" "}
          <span>{location}</span>
        </Category>
        {hasBootcampInfo ? (
          <HeadingButton
            ref={bootcampTriggerRef}
            type="button"
            $canHover={canHover}
            aria-haspopup="dialog"
            aria-expanded={isBootcampInfoOpen}
            aria-label={`${title}. Show Bootcamp information`}
            onMouseEnter={handleBootcampInfoMouseEnter}
            onMouseLeave={clearBootcampInfoHover}
            onClick={handleBootcampInfoClick}
            onKeyDown={handleBootcampInfoKeyDown}
          >
            {title}
          </HeadingButton>
        ) : (
          <Heading>{title}</Heading>
        )}
      </Header>
      <Description data={excerpt} />
      {showLastSpotsLabel && (
        <AvailabilityLabel>Last spots available</AvailabilityLabel>
      )}
      <Footer>
        <StyledButtonArrow
          ref={buttonRef}
          primary={type == "Bootcamp"}
          label={type == "Bootcamp" ? "Join now" : "Reserve your space"}
          to={generatePath(
            lang,
            type == "Bootcamp" ? "join-us" : "reserve-your-space"
          )}
          modal={{
            modal: windowWidth < 1023 ? false : true,
            formInput: `${title} | ${type} | ${startDateFormInput} | ${location} | ${startdateRaw}`,
          }}
        />
      </Footer>
      {hasBootcampInfo && (
        <BootcampInfo
          isOpen={isBootcampInfoOpen}
          onClose={() => setIsBootcampInfoOpen(false)}
          triggerRef={bootcampTriggerRef}
          lang={lang}
          ctaModal={{
            modal: windowWidth < 1023 ? false : true,
            formInput: `${title} | ${type} | ${startDateFormInput} | ${location} | ${startdateRaw}`,
          }}
        />
      )}
    </StyledItem>
  )
}

export default forwardRef(CalendarItem)
